import styles from "../style.module.scss";

interface Sequence {
  sequenceOrder: number;
  sequenceNames: string[];
  isSequenceCompleted: boolean;
}

interface SequenceStepperProps {
  sequences: Sequence[];
  activeSequence: number;
  onSequenceChange: (index: number) => void;
}

const SequenceStepper = ({
  sequences,
  activeSequence,
  onSequenceChange,
}: SequenceStepperProps) => {
  // Find the first incomplete sequence index - this is the "In Progress" sequence
  const firstIncompleteIndex = sequences.findIndex(
    (seq) => !seq.isSequenceCompleted
  );

  return (
    <div className={styles.stepperContainer}>
      {sequences.map((seq, index) => {
        const isCompleted = seq.isSequenceCompleted;
        const isActive = activeSequence === index;
        // A sequence is "In Progress" if it's the first incomplete one
        const isInProgress =
          !isCompleted && index === firstIncompleteIndex;
        // Disable tab if previous sequence is not completed (except first tab)
        const isPreviousCompleted =
          index === 0 || sequences[index - 1]?.isSequenceCompleted;
        const isDisabled = !isCompleted && !isPreviousCompleted;

        // Determine status text and style
        const getStatus = () => {
          if (isCompleted) return { text: "Completed", style: styles.stepStatusCompleted };
          if (isInProgress) return { text: "In Progress", style: styles.stepStatusInProgress };
          return { text: "Pending", style: styles.stepStatusPending };
        };

        const status = getStatus();

        return (
          <div
            key={seq.sequenceOrder}
            style={{ display: "flex", alignItems: "center" }}
          >
            <div
              className={`${styles.stepItem} ${
                isActive ? styles.stepItemActive : ""
              } ${isDisabled ? styles.stepItemDisabled : ""}`}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onClick={() => !isDisabled && onSequenceChange(index)}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onSequenceChange(index);
                }
              }}
            >
              <span
                className={`${styles.stepNumber} ${isActive
                    ? styles.stepNumberActive
                    : styles.stepNumberPending
                }`}
              >
                {index + 1}
              </span>
              <span className={styles.stepLabel}>{seq.sequenceNames.join(", ")}</span>
              <span className={`${styles.stepStatus} ${status.style}`}>
                {status.text}
              </span>
            </div>
            {index < sequences.length - 1 && (
              <span className={styles.stepArrow}>›</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SequenceStepper;
